import { types as api } from '@mesa-labs/mesa-api';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  AdvancedFilter,
  ClearAdvancedFilters,
  DownloadButton,
  Input,
  Pagination, PaginationDropdown, Search, Table, Typography,
  utils,
} from '@mesa-labs/mesa-ui';
import { ColumnProp } from '@mesa-labs/mesa-ui/dist/components/Table';

import { DefaultPaginationDropdownLimits } from '@mesa-labs/mesa-ui/dist/components/Pagination';
import Dropdown, { DropdownOption } from '@mesa-labs/mesa-ui/dist/components/Dropdown';
import {
  asExportFilter, useLazyExportAllExternalVendorsAsFacilitatorQuery,
  useGetAllExternalVendorsAsFacilitatorQuery,
} from '../../redux/api/facilitators';
import { useDispatch, useSelector } from '../../redux/hooks';
import {
  updateLimit, updatePage, updateSearchTerm, updateTotalPages, updateSortField,
  updateSortDirection, updateAddress, updateServiceType, updateLocale,
} from '../../redux/slices/vendors';
import { FilterSection, FilterControls } from '../common/Filters';
import { PaginationContainer } from '../common/Pagination';
import { Header } from '../common/Header';
import { DashboardEvents, trackEvent } from '../../analytics';

const VendorsPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.colors.White};
  border: 1px solid #EBEAED;
  border-radius: 12px;
  overflow: hidden;
`;

const AdvancedFilterContainer = styled.div`
  align-items: center;
  display: flex;  
  gap: 8px;
`;

const SearchContainer = styled.div`
  width: 600px;
`;

const LocaleDropdownOptions: DropdownOption<string>[] = [
  {
    label: 'No Selection',
    value: '',
  },
  ...(Object.keys(api.VendorLocale)).map((v: string) => ({
    label: v,
    value: v,
  })),
];

function VendorsPage(): React.ReactElement {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const partnerId = useSelector((state) => state.auth.partnerId);
  const searchTerm = useSelector((state) => state.vendors.searchTerm);
  const address = useSelector((state) => state.vendors.address);
  const serviceType = useSelector((state) => state.vendors.serviceType);
  const locale = useSelector((state) => state.vendors.locale);
  const page = useSelector((state) => state.vendors.page) || 1;
  const totalPages = useSelector((state) => state.vendors.totalPages);
  const limit = useSelector((state) => state.vendors.limit) || DefaultPaginationDropdownLimits[0];
  const sortField = useSelector((state) => state.vendors.sortField);
  const sortDirection = useSelector((state) => state.vendors.sortDirection);

  const [searchInput, setSearchInput] = useState(searchTerm || '');

  const [addressUpdate, setAddressUpdate] = useState(address);
  const [isAddressConfigured, setIsAddressConfigured] = useState(!!address);

  const [localeUpdate, setLocaleUpdate] = useState(locale);
  const [isLocaleConfigured, setIsLocaleConfigured] = useState(!!locale);

  const filter = useMemo(() => ({
    search: searchTerm,
    address: address || undefined,
    serviceType: serviceType || undefined,
    locale: (locale || undefined) as api.VendorLocale | undefined,
    sortField,
    sortDirection,
    limit: (typeof limit === 'number' ? limit : limit.value),
    page,
  }), [searchTerm, address, serviceType, locale, sortField, sortDirection, limit, page]);

  const {
    data: {
      data: vendors = [],
      total: totalVendors,
    } = {},
    isFetching,
  } = useGetAllExternalVendorsAsFacilitatorQuery({ accessToken: accessToken!, partnerId: partnerId!, ...filter }, { skip: !partnerId });

  const [exportAllExternalVendorsAsFacilitator] = useLazyExportAllExternalVendorsAsFacilitatorQuery();

  const VendorColumns: ColumnProp[] = useMemo(() => ([
    {
      key: 'externalVendorName',
      sortField: 'externalVendorName',
      label: 'Name',
    },
    {
      key: 'externalVendorId',
      label: 'Supplier ID',
    },
    {
      key: 'address',
      label: 'Address',
    },
    {
      key: 'activatedAt',
      sortField: 'activatedAt',
      label: 'Enablement Date',
      type: 'date',
      tooltip: 'An empty value indicates that the Supplier has signed up for Mesa but has not yet been enabled within JLL',
    },
    ...(partnerId !== api.Partners.JLL && [{
      key: 'totalInvoiceSpendT12m',
      sortField: 'totalInvoiceSpend',
      type: 'currency' as ColumnProp['type'],
      label: 'Total Spend',
    }] || []),
  ]), [partnerId]);


  const rows = useMemo(() => vendors.map((vendor) => ({
    id: vendor.externalVendorId,
    externalVendorName: vendor.externalVendorName,
    externalVendorId: vendor.externalVendorId,
    address: vendor.registeredAddressRegion,
    serviceType: vendor.serviceType,
    activatedAt: vendor.activatedAt,
    totalInvoiceSpendT12m: utils.formatCurrency(vendor.totalInvoiceSpendT12m, 'USD'),
  })), [vendors]);

  useEffect(() => {
    if (totalVendors !== undefined) {
      dispatch(updateTotalPages(Math.ceil(totalVendors / (typeof limit === 'number' ? limit : limit.value))));
    }
  }, [limit, totalVendors]);

  const onSubmitSearch = (value: string) => {
    const trimmed = value.trim();
    if (trimmed) {
      dispatch(updateSearchTerm(trimmed));
    } else {
      setSearchInput('');
      dispatch(updateSearchTerm(''));
    }
    dispatch(updatePage(1));
  };

  const onClearFilters = () => {
    setAddressUpdate('');
    setLocaleUpdate('');

    setIsAddressConfigured(false);
    setIsLocaleConfigured(false);

    dispatch(updateAddress(''));
    dispatch(updateServiceType(''));
    dispatch(updateLocale(''));
    dispatch(updatePage(1));
  };

  const appliedFilters = useMemo(() => [address, locale].reduce((acc: number, curr) => acc + (curr ? 1 : 0), 0), [address, locale]);

  const handleExport = async () => {
    const { data } = await exportAllExternalVendorsAsFacilitator({ partnerId: partnerId!, ...asExportFilter(filter) });
    return data;
  }

  useEffect(() => {
    trackEvent(DashboardEvents.SourcingSuppliersView);
  }, []);

  return (
    <>
      <Header>Supplier Search</Header>

      <VendorsPageContainer>
        <FilterSection
          filterControl={(
            <FilterControls>
              <SearchContainer>
                <Search
                  placeholder="Search by Supplier Name or Supplier ID. Press Enter to search, Escape to clear"
                  onChange={(value) => setSearchInput(value)}
                  onSubmit={onSubmitSearch}
                  value={searchInput}
                  expanded
                />
              </SearchContainer>
              <DownloadButton
                text="Download CSV"
                data={() => handleExport()}
                fileName="suppliers.csv"
                display="purple"
              />
            </FilterControls>
          )}
          appliedFilters={appliedFilters}
        >
          <AdvancedFilterContainer>
            <AdvancedFilter
              title="Filter by Address"
              buttonText="Address"
              configuredButtonText={address || ''}
              isConfigured={isAddressConfigured}
              onApply={() => {
                const trimmed = addressUpdate?.trim();
                if (trimmed) {
                  dispatch(updateAddress(trimmed));
                } else {
                  setAddressUpdate('');
                  dispatch(updateAddress(''));
                }
                dispatch(updatePage(1));
                setIsAddressConfigured(true);
              }}
              onClear={() => {
                setAddressUpdate('');
                dispatch(updateAddress(''));
                setIsAddressConfigured(false);
              }}
            >
              <Input
                type="text"
                placeholder="Filter by City or State"
                onChange={(evt) => setAddressUpdate(evt.target.value)}
                value={addressUpdate || ''}
              />
            </AdvancedFilter>

            <AdvancedFilter
              title="Filter by Locale"
              buttonText="Locale"
              configuredButtonText={locale || ''}
              isConfigured={isLocaleConfigured}
              onApply={() => {
                dispatch(updateLocale(localeUpdate || ''));
                dispatch(updatePage(1));
                setIsLocaleConfigured(true);
              }}
              onClear={() => {
                setLocaleUpdate('');
                dispatch(updateLocale(''));
                setIsLocaleConfigured(false);
              }}
            >
              <Dropdown
                options={LocaleDropdownOptions}
                selectedOption={localeUpdate}
                onChange={(l) => setLocaleUpdate(l)}
                width="100%"
              />
            </AdvancedFilter>

            <ClearAdvancedFilters
              isAnyConfigured={isAddressConfigured || isLocaleConfigured}
              onClear={onClearFilters}
            />
          </AdvancedFilterContainer>
        </FilterSection>

        <Table
          isLoading={isFetching}
          columns={VendorColumns}
          rows={rows}
          currentSortColumn={sortField}
          currentSortDirection={sortDirection}
          setSortField={(field: string) => dispatch(updateSortField(field))}
          setSortDirection={(direction: api.SortDirection) => dispatch(updateSortDirection(direction))}
          onRowClick={(row: Record<string, unknown>) => navigate(`/suppliers/${row.id}`)}
          paginationComponent={(
            <PaginationContainer>
              <PaginationDropdown
                selectedLimit={(typeof limit === 'number' ? DefaultPaginationDropdownLimits.find((l) => l.value === limit) || DefaultPaginationDropdownLimits[0] : limit)}
                onChange={(item) => {
                  dispatch(updateLimit(item));
                  dispatch(updatePage(1));
                }}
              />
              <Typography.BodySmallBold>
                Total of
                {' '}
                {totalVendors !== undefined ? totalVendors : '...'}
                {' '}
                Supplier(s)
              </Typography.BodySmallBold>
              <Pagination
                currentPage={page}
                totalPages={totalPages || 0}
                onPrev={() => dispatch(updatePage(page - 1))}
                onNext={() => dispatch(updatePage(page + 1))}
              />
            </PaginationContainer>
          )}
          customStyles={{
            columnsPadding: '24px',
            columnsBorderRadius: 0,
            paginationPadding: '0px',
            rowPadding: '0px 24px',
            columnColor: '#717680',
            columnFontSize: '12px',
            columnFontWeight: 600,
          }}
        />
      </VendorsPageContainer>
    </>
  );
}

export default VendorsPage;
